import { useLocation } from '@reach/router'
import React, { FunctionComponent, MouseEvent, useState } from 'react'
import { PuxAnchorLink } from 'Root/Shared/utils/PuxAnchorLink/PuxAnchorLink'
import { getModalTriggerId } from 'Shared/components/PuxModal/PuxModal'
import { getLocalizedUrl } from 'Shared/utils/localeURL'
import PuxLink from 'Shared/utils/PuxLink'

import PuxIcon, { IconSourceSize, PuxIconProps } from '../PuxIcon/PuxIcon'
import PuxLightbox from '../PuxLightbox/PuxLightbox'
import { getYTSubtitlesLocale } from '../PuxVideo/PuxVideo'

export type PuxButtonVariant =
  | `primary`
  | `border`
  | `danger`
  | `ghost`
  | `underline`

export type PuxButtonTheme = `default` | `inverse`
export type PuxButtonSize = `large` | `medium` | `small`

const BUTTON_SIZE_CLASS: Record<PuxButtonSize, string> = {
  large: `Button--lg`,
  medium: `Button--md`,
  small: `Button--sm`,
}

interface PuxButtonIconSizes {
  desktop: IconSourceSize
  mobile: IconSourceSize
}

const BUTTON_ICON_SIZES: Record<PuxButtonSize, PuxButtonIconSizes> = {
  large: { desktop: 24, mobile: 16 },
  medium: { desktop: 16, mobile: 12 },
  small: { desktop: 12, mobile: 12 },
}

const getPuxButtonIconSizes = (
  buttonSize: PuxButtonSize = `medium`
): PuxButtonIconSizes => BUTTON_ICON_SIZES[buttonSize]

export interface PuxButtonClassProps {
  buttonType?: PuxButtonVariant
  buttonTheme?: PuxButtonTheme
  buttonSize?: PuxButtonSize
  buttonDisabled?: boolean
  buttonClassName?: string
}

export const getPuxButtonClasses = ({
  buttonType = `primary`,
  buttonTheme = `default`,
  buttonSize = `medium`,
  buttonDisabled,
}: PuxButtonClassProps): string[] => {
  const classes = [
    `Button`,
    `Button--${buttonType}`,
    BUTTON_SIZE_CLASS[buttonSize],
  ]

  if (buttonTheme === `inverse`) {
    classes.push(`Button--inv`)
  }

  if (buttonDisabled) {
    classes.push(`is-disabled`)
  }

  return classes
}

export interface PuxButtonType extends PuxButtonClassProps {
  buttonIcon?: PuxIconProps['name']
  buttonNewWindow?: boolean
  buttonOpenModalId?: string
  buttonLink: {
    url: [string]
    internal: [boolean]
    text: [string]
  }
  onAnchorLinkClick?: () => void
}

const PuxButton: FunctionComponent<PuxButtonType> = (props) => {
  const target = props.buttonNewWindow ? `_blank` : undefined
  const url = props.buttonLink?.url?.[0]
  const text = props.buttonLink?.text?.[0]
  const isInternal = props.buttonLink?.internal?.[0]
  const classes = getPuxButtonClasses(props)
  const iconSizes = getPuxButtonIconSizes(props.buttonSize)
  const location = useLocation()
  const [videoToggler, setVideoToggler] = useState(false)
  const icon = props.buttonIcon ? (
    iconSizes.desktop === iconSizes.mobile ? (
      <PuxIcon
        className='button-icon'
        name={props.buttonIcon}
        size={iconSizes.desktop}
        decorative
      />
    ) : (
      <>
        <PuxIcon
          className='button-icon button-icon--mobile'
          name={props.buttonIcon}
          size={iconSizes.mobile}
          decorative
        />
        <PuxIcon
          className='button-icon button-icon--desktop'
          name={props.buttonIcon}
          size={iconSizes.desktop}
          decorative
        />
      </>
    )
  ) : null
  const content = (
    <>
      {text}
      {icon}
    </>
  )

  if (props.buttonClassName) {
    classes.push(props.buttonClassName)
  }

  const openModal = (event: MouseEvent<HTMLElement>) => {
    event.preventDefault()
    const trigger = document.getElementById(
      getModalTriggerId(props.buttonOpenModalId)
    )
    if (trigger) {
      trigger.click()
    } else {
      console.log(
        `Modal error: Section-modal with ID "${props.buttonOpenModalId}" not found`
      )
    }
  }

  if (!url && !text) return null

  if (url && isInternal) {
    return (
      <PuxLink
        className={classes.join(` `)}
        to={`${getLocalizedUrl(url)}`}
        target={target}
        onClick={props.buttonOpenModalId ? openModal : undefined}
        data-open-modal-id={props.buttonOpenModalId}
      >
        {content}
      </PuxLink>
    )
  }

  if (url && !isInternal && url.toLocaleLowerCase().startsWith(`yt:`)) {
    const yTLocale = getYTSubtitlesLocale()
    const videoID = url.replace(/^YT:/, ``).replace(/^yt:/, ``)
    const videoSources: JSX.Element[] = []
    videoSources.push(
      <iframe
        loading='lazy'
        style={{
          width: `90vw`,
          height: `${(90 / 16) * 9}vw`,
        }}
        src={`https://www.youtube-nocookie.com/embed/${videoID}?autoplay=1&modestbranding=1&rel=0&iv_load_policy=3&cc_load_policy=1&cc_lang_pref=${yTLocale}`}
        frameBorder='0'
        allow='autoplay; encrypted-media;'
      ></iframe>
    )

    return (
      <>
        <button
          type={`button`}
          className={classes.join(` `)}
          disabled={props.buttonDisabled}
          onClick={() => setVideoToggler(!videoToggler)}
        >
          {content}
        </button>
        <PuxLightbox
          toggler={videoToggler}
          sources={(videoSources as unknown) as string[]}
        />
      </>
    )
  }

  if (url && !isInternal && /^https?:/i.test(url)) {
    return (
      <a
        className={classes.join(` `)}
        href={url}
        target={target}
        rel={target === `_blank` ? `noopener` : undefined}
        onClick={props.buttonOpenModalId ? openModal : undefined}
        data-open-modal-id={props.buttonOpenModalId}
      >
        {content}
      </a>
    )
  }

  if (
    url &&
    !isInternal &&
    !/^https?:/i.test(url) &&
    !url.includes(`pux-modal`) &&
    url.includes(`#`)
  ) {
    const pathname = location.pathname
    const anchorPath = pathname + url

    return (
      <PuxAnchorLink
        className={classes.join(` `)}
        to={anchorPath.replace(`//#`, `/#`)}
        data-open-modal-id={props.buttonOpenModalId}
        onAnchorLinkClick={props.onAnchorLinkClick}
      >
        {content}
      </PuxAnchorLink>
    )
  }

  return (
    <button
      type={`button`}
      className={classes.join(` `)}
      disabled={props.buttonDisabled}
      onClick={props.buttonOpenModalId ? openModal : undefined}
      data-open-modal-id={props.buttonOpenModalId}
    >
      {content}
    </button>
  )
}

export default PuxButton
